<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <h6>{{ $t('sources.source_settings') }}</h6>
    </template>
    <template slot="dashboard-body">
      <div v-if="!isLoading" class="dashboard-content">
        <div class="d-flex align-items-center mb-4 pb-4">
          <img class="icon" v-bind:src="'/app/images/icons/sources/'+source.config.fileType+'.svg'" />
          <div>
            <h5 class="mb-3"><strong>{{ source.name }}</strong></h5>
            <p><strong>{{$t('sources.data_type')}}</strong> {{ source.config.fileType }}</p>
          </div>
        </div>
        <ValidationObserver ref="observer" v-slot="{ invalid }">
          <div class="source-edit-container d-flex mb-4">
            <div class="w-25">
              <p>{{ $t('sources.source_type') }}</p>
              <div class="d-flex mb-4 pb-4">
                <b-form-radio-group v-model="source.config.primary">
                  <b-form-radio id="primarySource" :value="true">{{ $t('sources.main_file') }}</b-form-radio>
                  <b-form-radio id="additionalSource" :value="false">{{ $t('sources.additional_file') }}</b-form-radio>
                </b-form-radio-group>
                <img class="tooltip-icon icon-small ml-3" id="tooltip-source-classification" v-bind:src="'/app/images/icons/info.svg'" />
                <b-popover target="tooltip-source-classification" variant="warning" triggers="hover" placement="bottom">
                  <div v-html="$t('tooltips.source_classification')"></div>
                </b-popover>
              </div>
            </div>
            <div class="source-edit-status">
              <p>{{ $t('sources.file_status') }}</p>
              <div class="d-flex">
                <b-form-radio-group v-model="source.active">
                  <b-form-radio id="activeSource" :value="true">{{ $t('sources.active') }}</b-form-radio>
                  <b-form-radio id="inactiveSource" :value="false">{{ $t('sources.inactive') }}</b-form-radio>
                </b-form-radio-group>
              </div>
            </div>
            <div v-if="!isDocs && isUrlVisible" class="source-edit-values">
              <p>{{ $t('sources.file_url') }}</p>
              <div class="d-flex">
                <validation-provider class="w-100 mb-4" mode="lazy" rules="required" v-slot="{ errors }">
                  <input v-model="source.config.url" class="form-control url-config-input" name="sourceFileName" type="text" :placeholder="$t('global.link')">
                  <span class="validation-error-required pl-2" v-if="errors[0] === 'sourceFileName is not valid.'">{{ $t('validations.required') }}</span>
                </validation-provider>
                <img class="tooltip-icon icon-small align-self-start ml-3 mt-2" id="tooltip-source-url" v-bind:src="'/app/images/icons/info.svg'" />
                <b-popover target="tooltip-source-url" variant="warning" triggers="hover" placement="bottom">
                  <div v-html="$t('tooltips.source_url')"></div>
                </b-popover>
              </div>
            </div>
            <div v-else-if="isDocs" class="source-edit-values">
              <div class="d-flex justify-content-between">
                <p>{{ $t('sources.google_sheet_url') }}</p>
                <p class="text-danger" v-show="!isGoogleSheetUrlValid">{{ $t('sources.invalid_google_sheet_url') }}</p>
              </div>
              <div class="mb-4">
                <validation-provider class="mb-4" mode="lazy" rules="required" v-slot="{ errors }">
                  <input v-model="sheetUrl" class="form-control sheets-url-config-input" name="docsKey" type="text">
                  <span class="validation-error-required pl-2" v-if="errors[0] === 'sourceFileName is not valid.'">{{ $t('validations.required') }}</span>
                </validation-provider>
              </div>
              <p>{{ $t('sources.worksheet_name') }}</p>
              <div>
                <validation-provider class="mb-4" mode="lazy" rules="required" v-slot="{ errors }">
                  <input v-model="source.config.docsConfig.worksheetName" class="form-control sheets-name-config-input" name="worksheetName" type="text">
                  <span class="validation-error-required pl-2" v-if="errors[0] === 'sourceFileName is not valid.'">{{ $t('validations.required') }}</span>
                </validation-provider>
              </div>
            </div>
          </div>
          <div class="source-edit-container d-flex justify-content-between pt-4">
            <router-link :to="{ name: 'sources', params: { id: $route.params.projectId }}">
              <button class="btn btn-large btn-gray-dark">
                <arrow-left-circle-icon></arrow-left-circle-icon>
                <span>{{ $t('sources.file_return') }}</span>
              </button>
            </router-link>
            <button class="btn btn-small btn-orange-dark" @click="updateSource" :disabled="invalid || !isGoogleSheetUrlValid">
              <save-icon></save-icon>
              <span>{{$t('global.save')}}</span>
            </button>
          </div>
        </ValidationObserver>
      </div>
      <div v-else>
        <div class="spinner-border text-orange" role="status"></div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'
import DashboardTemplate from '@/views/Dashboard'
import { ArrowLeftCircleIcon, SaveIcon } from 'vue-feather-icons'
import axios from 'axios'

extend('required', {
  ...required
})

export default {
  name: 'SourceEdit',

  data () {
    return {
      isDocs: false,
      isGoogleSheetUrlValid: true,
      isLoading: true,
      sheetUrl: '',
      source: {},
      sourceType: null,
      sourceUrl: ''
    }
  },

  created () {
    this.getProjectSources(this.$route.params.projectId, this.$route.params.sourceId)
  },

  methods: {
    getProjectSources (projectId, sourceId) {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/data/sources/' + projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.source = response.data.filter(function (source) {
              return source.id === Number(sourceId)
            })[0]
            if (this.source === undefined) {
              this.$router.push({ name: 'not-found' })
            } else {
              if (this.source.config.docsConfig !== undefined) {
                if (this.source.config.docsConfig.docsKey !== undefined) {
                  this.isDocs = true
                  this.sheetUrl = 'https://docs.google.com/spreadsheets/d/' + this.source.config.docsConfig.docsKey
                }
              }
              this.isLoading = false
            }
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    updateSource () {
      const sourceConfig = {
        name: this.source.name,
        active: this.source.active,
        description: '',
        config: this.source.config
      }

      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/sources/update/' + this.$route.params.projectId + '/' + this.source.id, sourceConfig)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$router.push({ name: 'sources', params: { id: this.$route.params.projectId } })
          }
        })
        .catch((error) => {
          if (this.isDocs) {
            this.$bvToast.toast(this.$t('sources.google_sheet_error_description') + error.response.data.message, {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('sources.google_sheet_error'),
              variant: 'danger'
            })
          } else {
            if (error.response.data.code === 13) {
              this.$bvToast.toast(this.$t('sources.source_edit_error_read'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_edit_error'),
                variant: 'danger'
              })
            } else if ((error.response.data.code === 4) || (error.response.data.code === 5) || (error.response.data.code === 6)) {
              this.$bvToast.toast(this.$t('sources.source_edit_error_parsing'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_edit_error'),
                variant: 'danger'
              })
            } else if (error.response.data.code === 26) {
              this.$bvToast.toast(this.$t('sources.source_edit_error_name'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_edit_error'),
                variant: 'danger'
              })
            } else {
              this.$bvToast.toast(this.$t('global.error_occured') + error.response.data.message, {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_edit_error'),
                variant: 'danger'
              })
            }
          }
        })
    }
  },

  computed: {
    isUrlVisible () {
      if ((this.source.config.fileType !== 'analytics') && (this.source.config.fileType !== 'docs')) return true
      else return false
    }
  },

  watch: {
    sheetUrl (newValue) {
      try {
        const matches = new RegExp('/spreadsheets/d/([a-zA-Z0-9-_]+)').exec(newValue)
        if (matches[1] !== null) this.source.config.docsConfig.docsKey = matches[1]
        this.isGoogleSheetUrlValid = true
      } catch (e) {
        this.isGoogleSheetUrlValid = false
      }
    }
  },

  components: { ArrowLeftCircleIcon, DashboardTemplate, SaveIcon, ValidationObserver, ValidationProvider }
}
</script>
